
/**
 * 订单地址位置卡（地图 + 导航）
 *
 * 职责：订单详情展示小地图 + Marker + 导航按钮。本组件不含任何厂商 API——
 * 一切经 services/map（MapService）：config / geocode 补全 / 渲染分发均在其内。
 *
 * 坐标优先级：
 *   1. 有 latitude/longitude（订单落库快照，GCJ-02）→ 直接渲染（天地图 Provider 内转坐标）
 *   2. 无坐标但有 address → MapService.resolveAddress 临时解析 → 渲染
 *      （逐级降级在后端：全址→去门牌→区→市→省，任一级命中即返回 + matched_level）
 *   3. 五级全灭 → 无地图区域（地址文本由页面层展示，导航按钮仍工作）
 *
 * 精度语义（2026-09-19）：
 *   matched_level = exact/road/district/city/province（快照坐标视为 exact）。
 *   首次 zoom 按精度分级（exact 16 / road 14 / district 12 / city 10 / province 8），
 *   之后用户自由缩放（interactive 全开：拖动/滚轮/双指/+/-，minZoom 4 ~ maxZoom 18）。
 *   非 exact 在地图左下角出轻提示（「已定位到南山区」），不弹大警告。
 *
 * 导航坐标门槛：仅 exact/road 坐标足够精准可作导航终点；district/city/province
 *   级坐标交给外部地图会指到行政区中心——此时不传坐标，让外部地图按完整地址
 *   自行检索（其 fuzzy 能力对中文地址更强）。地址文本永远原样透传，本组件
 *   不改写任何订单字段。
 *
 * 降级铁律（§13）：无 Provider / 无 Key / SDK 失败 / 解析失败 → 地图区域不渲染，
 * 不留"地图暂不可用"灰框；地址文本 + 导航按钮不受影响，绝不白屏、绝不把 Key 缺失等
 * 开发性错误暴露给用户。
 *
 * 坐标系：GCJ-02（legacy 默认，MapService 层声明）。本组件零坐标转换。
 */
import MapNavButton from '@/components/common/MapNavButton.vue'
import { getMapConfig, resolveAddress, createMap } from '@/services/map'

const COLLAPSED = 'collapsed' // 统一塌缩态：unavailable / no-location / no-address 一视同仁

// 首次定位 zoom 按精度分级（2026-09-19 用户规格：exact/road 较近、district 中等、
// city/province 较远；数值为天地图 z 级——16 街道建筑级 / 14 街区级 / 12 区级全貌 /
// 10 市级 / 8 省级）。仅决定首屏，用户此后自由缩放（4~18）
const ZOOM_BY_LEVEL = { exact: 16, road: 14, district: 12, city: 10, province: 8 }
const MIN_ZOOM = 4
const MAX_ZOOM = 18

export default {
  name: 'OrderLocationMap',
  components: { MapNavButton },
  props: {
    address: { type: String, default: '' },
    latitude: { type: [Number, String], default: null },
    longitude: { type: [Number, String], default: null },
    height: { type: Number, default: 200 }
  },
  data () {
    return {
      state: 'loading', // loading | ready | collapsed
      lat: this.latitude,
      lng: this.longitude,
      matchedLevel: null, // exact/road/district/city/province；快照坐标=null（视为 exact）
      matchedName: null, // 提示名（如「南山区」「深南大道」）；exact 无名
      viewHandle: null
    }
  },
  computed: {
    hasAddress () {
      return !!(this.address && String(this.address).trim())
    },
    // 精度轻提示文案：exact 不提示；road→「已定位到X路附近」；行政区级→「已定位到南山区」；
    // 无名兜底→「地址未精确匹配，已定位到所在区域」（不弹窗，不阻断，不重话）
    precisionHint () {
      const lv = this.matchedLevel
      if (!lv || lv === 'exact') return ''
      if (this.matchedName) {
        return lv === 'road' ? `已定位到${this.matchedName}附近` : `已定位到${this.matchedName}`
      }
      return '地址未精确匹配，已定位到所在区域'
    },
    // 导航坐标门槛（见文件头）：行政区级坐标会指到区/市中心，误导配送——不传坐标，
    // 外部地图按完整地址检索；exact/road（含订单快照坐标）正常精准定位
    navLat () {
      return this.navCoordOk ? this.lat : null
    },
    navLng () {
      return this.navCoordOk ? this.lng : null
    },
    navCoordOk () {
      if (this.lat == null || this.lng == null) return false
      return !this.matchedLevel || this.matchedLevel === 'exact' || this.matchedLevel === 'road'
    }
  },
  async mounted () {
    try {
      await this.initMap()
    } catch (e) {
      // 任何未预期异常 → 塌缩，绝不影响订单详情
      this.state = COLLAPSED
    }
  },
  beforeDestroy () {
    // 释放地图实例，避免内存泄漏
    if (this.viewHandle) {
      try { this.viewHandle.destroy() } catch (e) {}
      this.viewHandle = null
    }
  },
  methods: {
    async initMap () {
      // 0. 无坐标且无地址 → 塌缩（不发任何请求、不加载地图 JS）
      //    (0,0) 是历史脏数据哨兵值（非业务坐标）→ 视同无坐标（单点判定，步骤 2 复用）
      const latNum = Number(this.lat)
      const lngNum = Number(this.lng)
      const noCoord = isNaN(latNum) || isNaN(lngNum) || (latNum === 0 && lngNum === 0)
      if (noCoord && !this.hasAddress) {
        this.state = COLLAPSED
        return
      }
      // 1. 地图能力检查（无 Provider / 无 Key / 配置失败 → 折叠为 enabled:false）
      const cfg = await getMapConfig()
      if (!cfg.enabled) {
        this.state = COLLAPSED
        return
      }
      // 2. 确定坐标：props 有坐标直接用（快照=exact），无坐标→地址解析（后端逐级降级在其内）
      let lat = latNum
      let lng = lngNum
      if (noCoord) {
        const resolved = await resolveAddress(this.address)
        if (!resolved) {
          this.state = COLLAPSED
          return
        }
        lat = resolved.latitude
        lng = resolved.longitude
        this.lat = lat
        this.lng = lng
        this.matchedLevel = resolved.matchedLevel || null
        this.matchedName = resolved.matchedName || null
      }
      // 3. 渲染（Provider 内部加载厂商 SDK + 坐标边界转换；失败由 catch 塌缩）
      //    interactive 全开（2026-09-19 恢复）：pinch/滚轮/拖动/+/- 可用，minZoom/maxZoom
      //    约束范围不锁死；首次 zoom 按精度分级（快照坐标=exact）；正式导航仍走
      //    map-nav-btn（MapNavButton → 外部地图 URI），本组件不新增任何入口
      this.state = 'ready'
      const zoom = ZOOM_BY_LEVEL[this.matchedLevel || 'exact'] || 15
      this.$nextTick(async () => {
        try {
          this.viewHandle = await createMap(this.$refs.mapEl, {
            latitude: lat, longitude: lng, interactive: true, zoom, minZoom: MIN_ZOOM, maxZoom: MAX_ZOOM
          })
        } catch (e) {
          this.state = COLLAPSED
        }
      })
    }
  }
}
